import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    attrContainer?: GridContainerProps;
    attrItems?: GridItemProps;
    attrText?: TypographyPresentationProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {
        css: css`
            padding-bottom: 15px;
            /* border: 1px solid ${getColor("common.border")}; */
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
        fontFamily: "GibsonRegular",
    },
    attributeTypeValuesText: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            background-color: rgb(246 245 245);
            padding: 10px;
            border: 0.5px solid ${getColor("common.border")};
        `,
    },
    attrContainer: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 25px;
                    `,
                    css`
                        padding: 25px;
                    `,
                    css`
                        padding: 25px;
                    `,
                    css`
                        padding: 40px;
                    `,
                    css`
                        padding: 40px;
                    `,
                ])}
        `,
    },
    attrItems: {
        width: [12, 12, 12, 6, 6],
        css: css`
            padding: 5px !important;
        `,
    },
    attrText: {
        css: css`
            padding: 10px;
            width: fit-content;
            border: 1px solid ${getColor("common.border")};
            border-bottom: none;
            position: relative;
            top: 0.7px;
            background: #fff;
            margin-bottom: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 15px;
                        padding-right: 15px;
                    `,
                    css`
                        padding-left: 15px;
                        padding-right: 15px;
                    `,
                    css`
                        padding-left: 15px;
                        padding-right: 15px;
                    `,
                    css`
                        padding-left: 30px;
                        padding-right: 30px;
                    `,
                    css`
                        padding-left: 30px;
                        padding-right: 30px;
                    `,
                ])}
        `,
    },
};

const styles = attributesStyles;

const ProductDetailsAttributes: React.FC<Props> = ({ product: { brand, attributeTypes } }) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    const limitedAttributeTypes = attributeTypes?.filter(attributeType => attributeType.includeOnProduct) ?? [];

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_attributes">
            <Typography {...styles.attrText} variant="h3" as="span">
                {translate("Attributes")}
            </Typography>
            <GridContainer {...styles.attrContainer}>
                {brand && (
                    <GridItem {...styles.attrItems}>
                        <Typography data-test-selector="brand_item" as="p" {...styles.attributeTypeValuesText}>
                            <Typography data-test-selector="brand_item_label" {...styles.attributeTypeLabelText}>
                                {translate("Brand")}&nbsp;
                            </Typography>
                            <span data-test-selector="brand_item_value">{brand.name}</span>
                        </Typography>
                    </GridItem>
                )}

                {limitedAttributeTypes.map(attributeType => (
                    <GridItem key={attributeType.id} {...styles.attrItems}>
                        <Typography
                            key={attributeType.id.toString()}
                            data-test-selector="attributes_item"
                            data-attributetypeid={attributeType.id}
                            {...styles.attributeTypeValuesText}
                            as="p"
                        >
                            <Typography data-test-selector="attributes_item_label" {...styles.attributeTypeLabelText}>
                                {attributeType.label || attributeType.name}&nbsp;
                            </Typography>
                            <span data-test-selector="attributes_item_value">
                                {(attributeType.attributeValues ?? [])
                                    .map(attributeValue => attributeValue.valueDisplay)
                                    .join(", ")}
                            </span>
                        </Typography>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributes),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
